import {useCallback, useState, useMemo, useEffect, useRef} from 'react';

import {assert} from 'Asserts';
import {AgentType, LLMRole} from 'graphql-client/__generated/graphqlGlobalTypes';
import {trackMessageSent} from 'llm/chat-library/ChatAnalytics';
import * as ChatDAO from 'llm/chat-library/ChatDAO';
import {ChatFileUpload} from 'llm/chat-library/ChatLibraryTypes';
import {getLatestApplicationState, getLatestSystemState} from 'llm/chat-library/ChatStateUtil';
import {getInitialChatData, setInitialChatData} from 'llm/chat-library/InitialChatData';
import {
  ClientDisplayChatEvent,
  ClientOptimisticUserMessage,
  ClientProgressMessage,
  ClientStreamingAssistantMessage,
  DisplayMessageChatEventNode,
  ProgressStatus,
} from 'llm/chat-library/messages/MessageTypes';
import {
  createPendingUserMessage,
  createProgressMessage,
  createStreamingMessage,
} from 'llm/chat-library/messages/MessageUtils';
import {ProgressData} from 'llm/chat-library/progress/ProgressTypes';
import {
  ToolCallConfig,
  ToolCallsByAssistantMessage,
  getProgressToolCalls,
  getCurrentExchangeProgressData as getSharedCurrentExchangeProgressData,
  getToolCallsByAssistantMessage as getSharedToolCallsByAssistantMessage,
} from 'llm/chat-library/progress/ProgressUtils';
import {
  doesAgentTypeSupportPublicData,
  doesAgentTypeSupportSources,
} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import {ChatTypeByAgentType} from 'llm/chat-library/sidepanel/ChatTypeUtil';
import {useChatSessionEvents} from 'llm/chat-library/useChatSessionEvents';
import {
  getDataCaptureToolDisplayName,
  shouldDisplayDataCaptureTool,
} from 'llm/data-capture/DataCaptureToolTypes';
import {
  getDeepResearchToolDisplayName,
  shouldDisplayDeepResearchTool,
} from 'llm/deep-research/DeepResearchProgressUtils';
import {getToolCall as getDeepResearchToolCall} from 'llm/deep-research/tool-details/utils';
import notifications from 'notifications';
import * as Perf from 'performance/Perf';
import {RealtimeMessage} from 'realtime/RealtimeChannel';
import {useRealtimeRefetch} from 'realtime/RealtimeUpdateSubscriptionWrapper';
import useRealtimeChannelSubscription from 'realtime/useRealtimeChannelSubscription';
import {getRealtimeChannelNameForApiId} from 'realtime/util/RealtimeUtil';
import * as net from 'util/Net';

export interface SendMessageParams {
  text: string;
  fileUploads: Array<ChatFileUpload>;
  agentType: AgentType;
  /** Only relevant for agents that support public data sources */
  publicDataEnabled?: boolean;
  /** When true, archives previous failed messages before sending */
  retry?: boolean;
}

/**
 * Tool call configuration for different agent types.
 */
const TOOL_CALL_CONFIGS: Partial<Record<AgentType, ToolCallConfig>> = {
  [AgentType.DEEP_RESEARCH]: {
    shouldDisplayTool: shouldDisplayDeepResearchTool,
    getToolDisplayName: getDeepResearchToolDisplayName,
    parseToolCall: getDeepResearchToolCall,
  },
  [AgentType.ASK]: {
    shouldDisplayTool: shouldDisplayDeepResearchTool,
    getToolDisplayName: getDeepResearchToolDisplayName,
    parseToolCall: getDeepResearchToolCall,
  },
  [AgentType.DATA_CAPTURE]: {
    shouldDisplayTool: shouldDisplayDataCaptureTool,
    getToolDisplayName: getDataCaptureToolDisplayName,
  },
};

/**
 * Get the tool call configuration for a given agent type.
 * Falls back to Deep Research config as the default.
 */
function getToolCallConfigForAgent(agentType: AgentType | null): ToolCallConfig {
  if (agentType) {
    const config = TOOL_CALL_CONFIGS[agentType];
    if (config) {
      return config;
    }
  }
  // Default to Deep Research config
  return TOOL_CALL_CONFIGS[AgentType.DEEP_RESEARCH]!;
}

export interface UseMessageThreadOptions {
  chatSessionId: string;
  /**
   * When true, fetches tool call data from allThreads and derives progressData.
   * Enables activity/progress tracking UI for agents that use tools.
   */
  includeToolCalls?: boolean;
  /**
   * When true, disables realtime update subscriptions.
   * Used for read-only/locked chats where the user shouldn't receive live updates.
   */
  disableRealtimeUpdates?: boolean;
  /**
   * The primary agent type for this chat session.
   * Used to determine which tool call configuration to use for progress data.
   * If not provided, defaults to Deep Research configuration.
   */
  primaryAgentType?: AgentType;
}

export interface UseMessageThreadResult {
  /** Display events including optimistic user messages, streaming messages, and completed thinking sections */
  events: Array<ClientDisplayChatEvent>;
  /** Progress data for the current exchange (tool calls after last assistant message). Null if includeToolCalls is false or no data available. */
  progressData: ProgressData | null;
  /** All progress data from the session (used for citations, activity modal). Null if includeToolCalls is false or no data available. */
  allProgressData: ProgressData | null;
  /** Latest application state from StateUpdateChatEventNode events, derived from raw server events */
  latestApplicationState: object | null;
  isLoading: boolean;
  /** True when assistant is processing (thread status ACTIVE/QUEUED or pending message exists) */
  isAssistantRunning: boolean;
  errorMessage: string | null;
  /** True when the last display message is from the user (indicates a failed processing state) */
  isLastMessageFromUser: boolean;
  sendMessage: (params: SendMessageParams) => Promise<void>;
  refetch: () => void;
}

/**
 * Hook for managing chat message threads with realtime updates and streaming support.
 *
 * This hook provides:
 * - GraphQL polling (30s interval) as a fallback for data fetching
 * - Realtime-triggered refetch when backend publishes `llm_chat_update`
 * - Streaming message support for ASK mode (`llm_response_chunk`, `llm_response_complete`)
 * - Optimistic rendering of user messages
 * - Progress data derivation when `includeToolCalls` is enabled
 * - Completed thinking sections inserted before assistant messages
 *
 * NOTE: For optimal UX, the backend should call `LLMChatControllerSQLA.commit_and_publish_chat_update`
 * after significant state changes (tool calls, status changes, etc.) to trigger immediate refetches
 * rather than waiting for the 30s polling interval.
 */
export function useMessageThread({
  chatSessionId,
  includeToolCalls = false,
  disableRealtimeUpdates = false,
  primaryAgentType,
}: UseMessageThreadOptions): UseMessageThreadResult {
  // ============================================
  // 1. Initial Chat Data (optimistic rendering)
  // ============================================
  // If a chat was initiated via another component (e.g. NewChatInput), restore states for optimistic updates.
  // We do not reset this state when chatSessionId changes, as `ChatRouter` is rendered with a different `key`
  // when chat id changes.
  const initialChatData = getInitialChatData(chatSessionId);
  const initialMessage: ClientOptimisticUserMessage | null = useMemo(() => {
    if (!initialChatData) {
      return null;
    }
    return createPendingUserMessage({
      text: initialChatData.message.text,
      fileUploads: initialChatData.message.fileUploads,
      agentType: initialChatData.message.agentType,
    });
  }, [initialChatData]);

  const [pendingUserMessage, setPendingUserMessage] = useState<ClientOptimisticUserMessage | null>(
    initialMessage
  );

  // Handle initial chat data promise rejection
  const resetPendingOnFailure = useCallback(
    async (request?: Promise<{id: string}>) => {
      if (!request) {
        return;
      }
      try {
        await request;
      } catch (e) {
        if (net.isServerRequestError(e)) {
          notifications.notifyError(e);
          if (e.status >= 400 && e.status < 500) {
            setPendingUserMessage(null);
            setInitialChatData(chatSessionId, null);
          }
        } else {
          throw e;
        }
      }
    },
    [chatSessionId]
  );

  useEffect(() => {
    resetPendingOnFailure(initialChatData?.messagePromise);
  }, [initialChatData, resetPendingOnFailure]);

  // ============================================
  // 2. Streaming State
  // ============================================
  const [streamingMessage, setStreamingMessage] = useState<ClientStreamingAssistantMessage | null>(null);
  const receivedChunks = useRef<Map<number, string>>(new Map());
  const timeToFirstTokenPerfId = useRef<Perf.PerfId | null>(null);

  // ============================================
  // 3. Server Events via GraphQL
  // ============================================
  const {
    events: serverEvents,
    isLoading,
    errorMessage,
    threadStatus,
    refetch,
    chatSession,
    chatSessionQueryData,
  } = useChatSessionEvents({
    chatSessionId,
    includeToolCalls,
    onEventsUpdated: (events) => {
      // Clear pending user message when we receive server events
      if (events.length > 0) {
        setPendingUserMessage(null);
        // Also, clear the initial chat data so that if we leave and return to the chat,
        // we don't render the old initial chat data as a pending message again.
        setInitialChatData(chatSessionId, null);
      }

      // Clear streaming message if server has the final assistant message
      if (events.length > 0) {
        const lastServerEvent = events[events.length - 1];
        if (
          lastServerEvent.__typename === 'DisplayMessageChatEventNode' &&
          lastServerEvent.role === LLMRole.ASSISTANT
        ) {
          setStreamingMessage(null);
          receivedChunks.current.clear();
        }
      }
    },
  });

  // ============================================
  // 4. Realtime-Triggered Refetch
  // ============================================
  const isThreadActive = threadStatus === 'ACTIVE' || threadStatus === 'QUEUED';
  useRealtimeRefetch({
    apiId: chatSessionId,
    refetch,
    messageFilter: 'no_filter',
    hasIncompleteDataShouldRefetch: isThreadActive,
    disabled: disableRealtimeUpdates,
  });

  // ============================================
  // 5. Streaming Support
  // ============================================
  const handleStreamingChunk = useCallback(
    (data: {chunkNumber: number; content: string; userEventId: string}) => {
      // Report time-to-first-token metric on first chunk
      if (timeToFirstTokenPerfId.current && data.chunkNumber === 0) {
        Perf.markEnd(timeToFirstTokenPerfId.current);
        Perf.stop(timeToFirstTokenPerfId.current);
        timeToFirstTokenPerfId.current = null;
      }

      receivedChunks.current.set(data.chunkNumber, data.content);

      // Check if we have all chunks in sequence
      let completeContent = '';
      let hasAllChunks = true;
      for (let i = 0; i < receivedChunks.current.size; i++) {
        if (receivedChunks.current.has(i)) {
          completeContent += receivedChunks.current.get(i);
        } else {
          hasAllChunks = false;
          break;
        }
      }

      // Only render if we have all chunks in sequence
      if (hasAllChunks) {
        setStreamingMessage(
          createStreamingMessage({
            createdAt: new Date().toISOString(),
            agentType: AgentType.ASK,
            responseToEventId: data.userEventId,
            streamingContent: `${completeContent}▊`,
          })
        );
      }
    },
    []
  );

  const handleStreamingComplete = useCallback(
    (data: {content: string; userEventId: string; chatId: string}) => {
      setStreamingMessage(
        createStreamingMessage({
          createdAt: new Date().toISOString(),
          agentType: AgentType.ASK,
          responseToEventId: data.userEventId,
          streamingContent: data.content,
        })
      );
      receivedChunks.current.clear();

      // NB: We do not need to manually refetch messages at this point: the backend will broadcast a realtime
      // message, which will trigger a refetch via useRealtimeRefetch.
    },
    []
  );

  const onRealtimeMessage = useCallback(
    (message: RealtimeMessage) => {
      if (message.name === 'llm_response_chunk') {
        handleStreamingChunk(message.data);
      } else if (message.name === 'llm_response_complete') {
        handleStreamingComplete(message.data);
      }
    },
    [handleStreamingChunk, handleStreamingComplete]
  );

  useRealtimeChannelSubscription(
    !disableRealtimeUpdates ? getRealtimeChannelNameForApiId(chatSessionId) : '',
    onRealtimeMessage,
    'no_filter'
  );

  // ============================================
  // 6. Assistant Running State
  // ============================================
  const isAssistantRunning = pendingUserMessage != null || isThreadActive;

  // ============================================
  // 7. Tool Call Configuration
  // ============================================
  // Determine the agent type to use for tool call configuration
  const effectiveAgentType = useMemo(() => {
    // Use explicit primary agent type if provided
    if (primaryAgentType) {
      return primaryAgentType;
    }
    // Otherwise, infer from the last display message
    const lastDisplayEvent = serverEvents.findLast(
      (e): e is DisplayMessageChatEventNode => e.__typename === 'DisplayMessageChatEventNode'
    );
    return lastDisplayEvent?.agentType ?? pendingUserMessage?.agentType ?? null;
  }, [primaryAgentType, serverEvents, pendingUserMessage]);

  const toolCallConfig = useMemo(() => getToolCallConfigForAgent(effectiveAgentType), [effectiveAgentType]);

  // ============================================
  // 8. All Progress Data (for citations, activity modal)
  // ============================================
  const allProgressData = useMemo((): ProgressData | null => {
    if (!includeToolCalls || !chatSession) {
      return null;
    }

    const historyThread = getProgressToolCalls(chatSession, toolCallConfig);
    if (historyThread.length === 0) {
      return null;
    }

    return {historyThread};
  }, [includeToolCalls, chatSession, toolCallConfig]);

  // ============================================
  // 9. Current Exchange Progress Data (for in-progress thinking)
  // ============================================
  const progressData = useMemo((): ProgressData | null => {
    if (!includeToolCalls) {
      return null;
    }

    // When we have a pending message but no server events yet, return empty progress
    if (pendingUserMessage && !chatSessionQueryData) {
      return {historyThread: []};
    }

    if (!chatSessionQueryData) {
      return null;
    }

    return getSharedCurrentExchangeProgressData(chatSessionQueryData, serverEvents, toolCallConfig);
  }, [includeToolCalls, chatSessionQueryData, serverEvents, pendingUserMessage, toolCallConfig]);

  // ============================================
  // 10. Tool Calls By Assistant Message (for completed thinking sections)
  // ============================================
  const toolCallsByAssistantMessage = useMemo((): ToolCallsByAssistantMessage => {
    if (!includeToolCalls || !chatSessionQueryData) {
      return {};
    }
    return getSharedToolCallsByAssistantMessage(chatSessionQueryData, serverEvents, toolCallConfig);
  }, [includeToolCalls, chatSessionQueryData, serverEvents, toolCallConfig]);

  // ============================================
  // 11. Latest Application State
  // ============================================
  // Derive application state from raw serverEvents (not filtered events) to include StateUpdateChatEventNode
  const latestApplicationState = useMemo(() => getLatestApplicationState(serverEvents), [serverEvents]);

  // ============================================
  // 12. Progress Message (for in-progress or error states)
  // ============================================
  const progressMessage = useMemo((): ClientProgressMessage | null => {
    const systemState = getLatestSystemState(serverEvents);
    const systemErrorMessage = systemState?.error_message;

    if (systemErrorMessage) {
      const lastDisplayEvent = serverEvents.findLast(
        (e): e is DisplayMessageChatEventNode => e.__typename === 'DisplayMessageChatEventNode'
      );
      return createProgressMessage({
        status: ProgressStatus.FAILED,
        errorMessage: systemErrorMessage,
        agentType: lastDisplayEvent?.agentType ?? pendingUserMessage?.agentType ?? null,
        progressData,
      });
    }

    // Show progress if last message is from user (either pending or persisted)
    const lastDisplayEvent =
      pendingUserMessage ??
      serverEvents.findLast(
        (e): e is DisplayMessageChatEventNode => e.__typename === 'DisplayMessageChatEventNode'
      );

    if (lastDisplayEvent && lastDisplayEvent.role === LLMRole.USER) {
      // Use IN_PROGRESS when we have tool calls or thread is active, STARTED otherwise
      const hasToolCalls = progressData && progressData.historyThread.length > 0;
      const status =
        hasToolCalls || threadStatus === 'ACTIVE' ? ProgressStatus.IN_PROGRESS : ProgressStatus.STARTED;

      return createProgressMessage({
        status,
        errorMessage: null,
        agentType: lastDisplayEvent.agentType,
        progressData,
      });
    }

    return null;
  }, [serverEvents, pendingUserMessage, threadStatus, progressData]);

  // ============================================
  // 13. Compose Final Events (with completed thinking sections)
  // ============================================
  const events = useMemo<Array<ClientDisplayChatEvent>>(() => {
    const displayEvents = serverEvents.filter(
      (e): e is DisplayMessageChatEventNode => e.__typename === 'DisplayMessageChatEventNode'
    );

    const result: Array<ClientDisplayChatEvent> = [];

    // Insert completed thinking sections before each assistant message
    for (const event of displayEvents) {
      if (event.role === LLMRole.ASSISTANT) {
        const toolCallsForMessage = toolCallsByAssistantMessage[event.id];
        if (toolCallsForMessage && toolCallsForMessage.historyThread.length > 0) {
          const thinkingMessage: ClientProgressMessage = createProgressMessage({
            status: ProgressStatus.COMPLETED,
            errorMessage: null,
            agentType: event.agentType,
            progressData: toolCallsForMessage,
            associatedMessageId: event.id,
          });
          result.push(thinkingMessage);
        }
      }
      result.push(event);
    }

    // Append optimistic user message if present
    if (pendingUserMessage) {
      result.push(pendingUserMessage);
    }

    // Insert streaming message if present (and we don't already have the final message)
    if (streamingMessage) {
      result.push(streamingMessage);
    }

    // Append progress message for in-progress or error states
    if (progressMessage) {
      result.push(progressMessage);
    }

    return result;
  }, [serverEvents, pendingUserMessage, streamingMessage, progressMessage, toolCallsByAssistantMessage]);

  // ============================================
  // 14. Last Message From User Detection
  // ============================================
  // Check if the chat is in an error state where the last message is from the user
  // and there's no assistant response. This indicates a failed processing state
  // where retry functionality should be available.
  const isLastMessageFromUser = useMemo(() => {
    const lastDisplayMessage = serverEvents.findLast(
      (event) => event.__typename === 'DisplayMessageChatEventNode'
    );
    return lastDisplayMessage != null && lastDisplayMessage.role === LLMRole.USER;
  }, [serverEvents]);

  // ============================================
  // 15. Send Message
  // ============================================
  const sendMessage = useCallback(
    async (params: SendMessageParams) => {
      const {text, fileUploads, agentType, publicDataEnabled = false, retry = false} = params;

      assert(pendingUserMessage == null, 'Cannot send message while pending user message is present');

      // Create optimistic user message
      const optimisticMessage = createPendingUserMessage({
        text,
        fileUploads,
        agentType,
      });
      setPendingUserMessage(optimisticMessage);

      // Start time-to-first-token tracking for ASK mode
      if (agentType === AgentType.ASK) {
        const perfId = Perf.start('chat.ask.ttft_ms', {enabled: false}) ?? '';
        timeToFirstTokenPerfId.current = perfId;
        Perf.markStart(perfId);
      }

      // Track analytics
      const chatType = ChatTypeByAgentType[agentType];
      trackMessageSent({
        chatType,
        agentType,
        messageCount: serverEvents.filter((e) => e.__typename === 'DisplayMessageChatEventNode').length,
        fileCount: fileUploads.length,
        includePublicData: doesAgentTypeSupportPublicData(agentType) ? publicDataEnabled : null,
      });

      const sources = doesAgentTypeSupportSources(agentType)
        ? doesAgentTypeSupportPublicData(agentType) && publicDataEnabled
          ? [{type: 'public_data' as const}]
          : []
        : undefined;

      // Send to server
      try {
        await ChatDAO.appendDisplayMessageToChatSession({
          chatSessionId,
          agentType,
          messageBlocks: ChatDAO.getMessageBlockInput({text, fileUploads}),
          userChatContext: ChatDAO.getUserChatContext({
            contextIds: ChatDAO.getContextIdsFromActiveWorkspace(),
          }),
          sources,
          retry,
        });
      } catch (e) {
        if (net.isServerRequestError(e)) {
          notifications.notifyError(e);
          if (e.status >= 400 && e.status < 500) {
            setPendingUserMessage(null);
          }
        } else {
          throw e;
        }
      }
    },
    [chatSessionId, serverEvents, pendingUserMessage]
  );

  return {
    events,
    progressData,
    allProgressData,
    latestApplicationState,
    isLoading,
    isAssistantRunning,
    errorMessage,
    isLastMessageFromUser,
    sendMessage,
    refetch,
  };
}
